import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { serviceCategoryService } from "@/services/serviceCategoryService";
import {
  CreateServiceCategoryDto,
  ServiceCategoriesFilters,
  UpdateServiceCategoryDto,
} from "@/services/interfaces/IServiceCategoryService";

export const serviceCategoriesKeys = {
  all: ["serviceCategories"] as const,
  lists: () => [...serviceCategoriesKeys.all, "list"] as const,
  list: (filters?: ServiceCategoriesFilters) =>
    [...serviceCategoriesKeys.lists(), filters] as const,
  details: () => [...serviceCategoriesKeys.all, "detail"] as const,
  detail: (id: string) => [...serviceCategoriesKeys.details(), id] as const,
};

export const useServiceCategories = (
  filters?: ServiceCategoriesFilters,
  options?: { enabled?: boolean },
) => {
  return useQuery({
    queryKey: serviceCategoriesKeys.list(filters),
    queryFn: () => serviceCategoryService.getAll(filters),
    enabled: Boolean(filters?.hotelId) && (options?.enabled ?? true),
  });
};

export const useServiceCategory = (id: string) => {
  return useQuery({
    queryKey: serviceCategoriesKeys.detail(id),
    queryFn: () => serviceCategoryService.getById(id),
    enabled: !!id,
  });
};

export const useCreateServiceCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (data: CreateServiceCategoryDto) =>
      serviceCategoryService.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: serviceCategoriesKeys.lists(),
      });
    },
  });
};

export const useUpdateServiceCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({
      id,
      data,
    }: {
      id: string;
      data: UpdateServiceCategoryDto;
    }) => serviceCategoryService.update(id, data),
    onSuccess: (updatedCategory) => {
      queryClient.setQueryData(
        serviceCategoriesKeys.detail(updatedCategory.id),
        updatedCategory,
      );
      queryClient.invalidateQueries({
        queryKey: serviceCategoriesKeys.lists(),
      });
    },
  });
};

export const useDeleteServiceCategory = () => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: (id: string) => serviceCategoryService.deleteCategory(id),
    onSuccess: (_, deletedId) => {
      queryClient.removeQueries({
        queryKey: serviceCategoriesKeys.detail(deletedId),
      });
      queryClient.invalidateQueries({
        queryKey: serviceCategoriesKeys.lists(),
      });
    },
  });
};
